import React from 'react';
import { styled, useMediaQuery } from '@mui/material';
import theme from 'theme';
import { Divider, Row, Stack, Title } from 'library/ui';
import PublicTeacherCard from './PublicTeacherCard';
import FindingTeachersLoader from './FindingTeachersLoader';
import { ITeacher } from 'models/Providers/Teacher';
import NoWorkersFound from './NoWorkersFound';
import SuccessMessage from './SuccessMessage';
import { SeeMoreTeachers } from './SeeMoreTeachers';
import { StaffFilterOptions } from 'parent-portal/shared/filterOptions';
import PublicFilters from './PublicFilters';

export function StaffNearYou({
    signUp,
    zipCode,
    searchedZipCode,
    updateZipCode,
    onSearch,
    loading,
    workers,
    vettedWorkersCount,
    filters,
}: {
    signUp: () => void;
    zipCode: string;
    searchedZipCode: string;
    updateZipCode: (value: string) => void;
    onSearch: (filters: StaffFilterOptions) => void;
    loading: boolean;
    workers?: ITeacher[];
    vettedWorkersCount: number;
    filters: StaffFilterOptions;
}) {
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const titleAndSuccess = (
        <>
            <Title size="h1">Available Teachers Near {searchedZipCode}</Title>
            <SuccessMessage visible={!loading && !!workers && workers.length > 0} />
        </>
    );

    const resultsContent = (
        <Stack gap="none" fullWidth padding="lg">
            {loading && <FindingTeachersLoader zipCode={searchedZipCode} />}
            {!loading && (!workers || workers.length === 0) && <NoWorkersFound signUp={signUp} />}
            <TeacherFade $loading={loading}>
                <Stack gap="sm">
                    {!loading &&
                        workers?.slice(0, 10).map((worker) => <PublicTeacherCard key={worker.id} worker={worker} />)}
                </Stack>
            </TeacherFade>
            {!loading && workers && workers.length > 0 && (
                <SeeMoreTeachers signUp={signUp} vettedWorkersCount={vettedWorkersCount} />
            )}
        </Stack>
    );

    const filtersContent = (
        <Stack padding="md" align="center">
            <PublicFilters zipCode={zipCode} updateZipCode={updateZipCode} filters={filters} onSearch={onSearch} />
        </Stack>
    );

    if (!isDesktop) {
        return (
            <Stack align="center" gap="lg" maxWidth="lg" fullWidth padding="sm">
                {titleAndSuccess}
                {filtersContent}
                {resultsContent}
            </Stack>
        );
    }

    return (
        <Stack align="center">
            <Stack maxWidth="lg" align="center" padding="lg">
                <Row align="start" gap="sm">
                    {filtersContent}
                    <Divider orientation="vertical" />
                    <Stack align="center" gap="lg" grow>
                        {titleAndSuccess}
                        {resultsContent}
                    </Stack>
                </Row>
            </Stack>
        </Stack>
    );
}

const TeacherFade = styled('div')<{ $loading: boolean }>(({ $loading }) => ({
    width: '100%',
    opacity: $loading ? 0.4 : 1,
    transition: 'opacity 0.2s ease',
}));
